/**
 * Binary container for pre-apply ("before") socket meshes.
 *
 * These blobs exist only so Undo can restore the socket on a device that never
 * saw the apply. Nothing outside this app ever reads them, so they deliberately
 * do **not** use PLY/STL:
 *
 *  - the PLY exporter writes `format ascii 1.0`, which for a ~150k-vertex socket
 *    is ~4-5x the size of the raw buffers and needs a text parse on the way back;
 *  - a raw typed-array dump is byte-for-byte what `BufferGeometry` already holds,
 *    so unpacking is a set of `subarray` views with no numeric parsing at all.
 *
 * Two further size choices, both lossless for our purposes:
 *
 *  - **normals are dropped.** `deserializeGeometry`/this module recompute them,
 *    and a recomputed normal is what the tools operate on anyway.
 *  - **colors are quantised to `Uint8`.** Three.js keeps the colour attribute as
 *    Float32 0..1, but the source scans carry 8-bit colour and the PLY exporter
 *    writes `uchar` — so the extra 24 bits per component never held information.
 *
 * Every other attribute (uv in particular — the logo projection needs it) is
 * preserved as Float32.
 *
 * Layout:
 *   [0..7]    magic "BXUNDO1\0"
 *   [8..11]   header byte length (Uint32, little endian)
 *   [12..]    header JSON (utf8)
 *   [...]     attribute/index buffers, each 4-byte aligned, in header order
 */
import * as THREE from 'three';

const LOG_PREFIX = '[UndoMeshBlob]';

const MAGIC = 'BXUNDO1\0';
const MAGIC_BYTES = 8;
const HEADER_LENGTH_BYTES = 4;

/** Bump when the container layout changes; older blobs are then ignored. */
export const MESH_BLOB_VERSION = 1;

/** Recomputed on unpack — never worth the bytes. */
const SKIPPED_ATTRIBUTES = new Set(['normal']);

function align4(n) {
  return (n + 3) & ~3;
}

/**
 * Colour components as bytes. Handles both storage shapes Three.js may hand us:
 * normalized integer arrays (already 0..255) and float arrays (0..1).
 */
function colorToBytes(attr) {
  const src = attr.array;
  if (src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    return new Uint8Array(src);
  }
  const out = new Uint8Array(src.length);
  for (let i = 0; i < src.length; i++) {
    // Math.round rather than truncate: 1.0 must land on 255, not 254.
    const v = Math.round(src[i] * 255);
    out[i] = v < 0 ? 0 : v > 255 ? 255 : v;
  }
  return out;
}

function bytesToColor(bytes) {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = bytes[i] / 255;
  return out;
}

/**
 * Smallest index type that still addresses every vertex. Uint16 halves the index
 * buffer, which on a decimated socket is the single largest chunk after positions.
 */
function packIndex(indexArray, vertexCount) {
  if (indexArray instanceof Uint16Array) return { type: 'u16', array: indexArray };
  if (vertexCount <= 65535) return { type: 'u16', array: Uint16Array.from(indexArray) };
  return { type: 'u32', array: indexArray instanceof Uint32Array ? indexArray : Uint32Array.from(indexArray) };
}

function typedArrayFor(type, buffer, byteOffset, length) {
  switch (type) {
    case 'f32':
      return new Float32Array(buffer, byteOffset, length);
    case 'u8':
      return new Uint8Array(buffer, byteOffset, length);
    case 'u16':
      return new Uint16Array(buffer, byteOffset, length);
    case 'u32':
      return new Uint32Array(buffer, byteOffset, length);
    default:
      return null;
  }
}

/**
 * Pack a pre-apply payload into one transferable ArrayBuffer.
 *
 * @param {object} payload  same shape the in-memory undo pool holds.
 * @param {THREE.BufferGeometry} payload.geometry
 * @param {number[]} [payload.position]
 * @param {number[]} [payload.quaternion]
 * @param {number[]} [payload.scale]
 * @param {string} [payload.meshName]
 * @param {object} [payload.userData]
 * @param {object|null} [payload.materialData]
 * @returns {ArrayBuffer | null}
 */
export function packMeshBlob(payload) {
  const geometry = payload?.geometry;
  const positionAttr = geometry?.attributes?.position;
  if (!positionAttr?.count) {
    console.warn(`${LOG_PREFIX} refusing to pack — geometry has no positions`);
    return null;
  }

  const vertexCount = positionAttr.count;
  /** @type {{ name: string, type: string, itemSize: number, normalized: boolean, array: ArrayBufferView }[]} */
  const chunks = [];

  for (const name of Object.keys(geometry.attributes)) {
    if (SKIPPED_ATTRIBUTES.has(name)) continue;
    const attr = geometry.attributes[name];
    if (!attr?.array?.length) continue;

    if (name === 'color') {
      chunks.push({
        name,
        type: 'u8',
        itemSize: attr.itemSize,
        normalized: false, // expanded back to float on unpack
        array: colorToBytes(attr),
      });
      continue;
    }

    chunks.push({
      name,
      type: 'f32',
      itemSize: attr.itemSize,
      normalized: !!attr.normalized,
      array: attr.array instanceof Float32Array ? attr.array : Float32Array.from(attr.array),
    });
  }

  let indexChunk = null;
  if (geometry.index?.array?.length) {
    const packed = packIndex(geometry.index.array, vertexCount);
    indexChunk = { type: packed.type, array: packed.array };
  }

  // First pass: offsets. Header is written afterwards because it records them.
  const header = {
    v: MESH_BLOB_VERSION,
    vertexCount,
    triangleCount: indexChunk
      ? indexChunk.array.length / 3
      : Math.floor(vertexCount / 3),
    attributes: {},
    index: null,
    transform: {
      position: payload.position ?? [0, 0, 0],
      quaternion: payload.quaternion ?? [0, 0, 0, 1],
      scale: payload.scale ?? [1, 1, 1],
    },
    meshName: payload.meshName ?? 'ShadedMesh',
    userData: payload.userData ?? {},
    materialData: payload.materialData ?? null,
  };

  let cursor = 0;
  for (const chunk of chunks) {
    header.attributes[chunk.name] = {
      type: chunk.type,
      itemSize: chunk.itemSize,
      normalized: chunk.normalized,
      length: chunk.array.length,
      offset: cursor,
    };
    cursor = align4(cursor + chunk.array.byteLength);
  }
  if (indexChunk) {
    header.index = {
      type: indexChunk.type,
      length: indexChunk.array.length,
      offset: cursor,
    };
    cursor = align4(cursor + indexChunk.array.byteLength);
  }

  const headerBytes = new TextEncoder().encode(JSON.stringify(header));
  const headerStart = MAGIC_BYTES + HEADER_LENGTH_BYTES;
  const payloadStart = align4(headerStart + headerBytes.length);
  const total = payloadStart + cursor;

  const buffer = new ArrayBuffer(total);
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  for (let i = 0; i < MAGIC_BYTES; i++) bytes[i] = MAGIC.charCodeAt(i);
  view.setUint32(MAGIC_BYTES, headerBytes.length, true);
  bytes.set(headerBytes, headerStart);

  for (const chunk of chunks) {
    const { offset } = header.attributes[chunk.name];
    bytes.set(
      new Uint8Array(chunk.array.buffer, chunk.array.byteOffset, chunk.array.byteLength),
      payloadStart + offset,
    );
  }
  if (indexChunk) {
    bytes.set(
      new Uint8Array(
        indexChunk.array.buffer,
        indexChunk.array.byteOffset,
        indexChunk.array.byteLength,
      ),
      payloadStart + header.index.offset,
    );
  }

  return buffer;
}

/**
 * Reverse of {@link packMeshBlob}.
 *
 * Returns a payload in exactly the shape `undoSnapshot`'s pool holds, so a
 * downloaded blob and an in-memory snapshot are interchangeable from there on.
 *
 * @param {ArrayBuffer} buffer
 * @returns {{ geometry: THREE.BufferGeometry, position: number[], quaternion: number[], scale: number[], meshName: string, userData: object, materialData: object|null } | null}
 */
export function unpackMeshBlob(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < MAGIC_BYTES + HEADER_LENGTH_BYTES) {
    console.warn(`${LOG_PREFIX} not a mesh blob — too short`);
    return null;
  }

  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < MAGIC_BYTES; i++) {
    if (bytes[i] !== MAGIC.charCodeAt(i)) {
      console.warn(`${LOG_PREFIX} bad magic — not a mesh blob`);
      return null;
    }
  }

  const view = new DataView(buffer);
  const headerLength = view.getUint32(MAGIC_BYTES, true);
  const headerStart = MAGIC_BYTES + HEADER_LENGTH_BYTES;
  if (headerStart + headerLength > buffer.byteLength) {
    console.warn(`${LOG_PREFIX} header length overruns buffer`);
    return null;
  }

  let header;
  try {
    header = JSON.parse(
      new TextDecoder().decode(new Uint8Array(buffer, headerStart, headerLength)),
    );
  } catch (e) {
    console.warn(`${LOG_PREFIX} header parse failed`, e);
    return null;
  }

  if (header?.v !== MESH_BLOB_VERSION) {
    console.warn(`${LOG_PREFIX} ignoring blob — container version ${header?.v}`);
    return null;
  }

  const payloadStart = align4(headerStart + headerLength);
  const geometry = new THREE.BufferGeometry();

  for (const [name, spec] of Object.entries(header.attributes ?? {})) {
    const byteOffset = payloadStart + spec.offset;
    const arr = typedArrayFor(spec.type, buffer, byteOffset, spec.length);
    if (!arr) {
      console.warn(`${LOG_PREFIX} unknown attribute type ${spec.type} for ${name}`);
      continue;
    }
    // Copy out of the container: the caller may hold the payload long after the
    // source ArrayBuffer is released, and views would pin the whole blob.
    if (name === 'color' && spec.type === 'u8') {
      geometry.setAttribute(
        name,
        new THREE.BufferAttribute(bytesToColor(arr), spec.itemSize, false),
      );
      continue;
    }
    geometry.setAttribute(
      name,
      new THREE.BufferAttribute(arr.slice(), spec.itemSize, !!spec.normalized),
    );
  }

  if (!geometry.attributes.position) {
    console.warn(`${LOG_PREFIX} blob has no position attribute`);
    return null;
  }

  if (header.index) {
    const arr = typedArrayFor(
      header.index.type,
      buffer,
      payloadStart + header.index.offset,
      header.index.length,
    );
    if (arr) geometry.setIndex(new THREE.BufferAttribute(arr.slice(), 1));
  }

  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  return {
    geometry,
    position: header.transform?.position ?? [0, 0, 0],
    quaternion: header.transform?.quaternion ?? [0, 0, 0, 1],
    scale: header.transform?.scale ?? [1, 1, 1],
    meshName: header.meshName ?? 'ShadedMesh',
    userData: header.userData ?? {},
    materialData: header.materialData ?? null,
  };
}

/**
 * Byte size `packMeshBlob` would produce, without allocating it. Used by the
 * router to weigh "download the before-mesh" against "replay the recipe".
 *
 * @param {THREE.BufferGeometry} geometry
 * @returns {number}
 */
export function estimateBlobBytes(geometry) {
  if (!geometry?.attributes?.position?.count) return 0;
  const vertexCount = geometry.attributes.position.count;
  let bytes = 512; // header allowance

  for (const name of Object.keys(geometry.attributes)) {
    if (SKIPPED_ATTRIBUTES.has(name)) continue;
    const attr = geometry.attributes[name];
    if (!attr?.array?.length) continue;
    bytes += name === 'color' ? attr.array.length : attr.array.length * 4;
  }
  if (geometry.index?.array?.length) {
    bytes += geometry.index.array.length * (vertexCount <= 65535 ? 2 : 4);
  }
  return bytes;
}
